import { useNavigate } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ArrowLeft, Settings as SettingsIcon, Building, Hash, Database } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { format } from 'date-fns';

export function SettingsScreen() {
    const navigate = useNavigate();
    const { selectedBranchId, selectedDate } = useApp();

    const { data: branches } = trpc.listBranches.useQuery();

    // We fetch seats for current branch just to count them
    const { data: seatData, isLoading: isLoadingSeats } = trpc.listSeats.useQuery(
        { date: format(selectedDate, 'yyyy-MM-dd'), branchId: selectedBranchId! },
        { enabled: !!selectedBranchId }
    );

    const currentBranch = branches?.find(b => b.id === selectedBranchId);

    return (
        <div className="min-h-screen bg-background pb-20">
            <div className="flex items-center gap-4 p-4 border-b bg-background/80 backdrop-blur-md sticky top-0 z-10">
                <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <h1 className="text-xl font-bold">Settings</h1>
            </div>

            <div className="p-4 space-y-6">

                {/* Deployment Info */}
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-base flex items-center gap-2">
                                <Database className="h-4 w-4" /> Tenant Deployment
                            </CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <div className="text-muted-foreground">Tenant Slug</div>
                                <div className="font-mono bg-muted px-2 py-1 rounded inline-block mt-1">demo-library</div>
                            </div>
                            <div>
                                <div className="text-muted-foreground">Environment</div>
                                <div className="font-medium mt-1">Production</div>
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            This identifier is used to isolate your data.
                        </p>
                    </CardContent>
                </Card>

                {/* Branch Info */}
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-base flex items-center gap-2">
                                <Building className="h-4 w-4" /> Current Branch
                            </CardTitle>
                            {currentBranch && (
                                <span className="text-xs font-normal text-muted-foreground bg-secondary px-2 py-1 rounded-full">
                                    {currentBranch.name}
                                </span>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {!selectedBranchId ? (
                            <div className="text-sm text-yellow-600">No branch selected. Please select a branch from the dashboard.</div>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-3 border rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                            <Hash className="h-4 w-4 text-primary" />
                                        </div>
                                        <div>
                                            <div className="font-medium">Total Seats</div>
                                            <div className="text-xs text-muted-foreground">Capacity of this branch</div>
                                        </div>
                                    </div>
                                    <div className="text-2xl font-bold">
                                        {isLoadingSeats ? "..." : seatData?.seats.length || 0}
                                    </div>
                                </div>

                                <div className="p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
                                    To edit seat layout or capacity, please contact the administrator or use the branch management portal.
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Account / Support */}
                <div className="space-y-2">
                    <Button variant="outline" className="w-full justify-start text-muted-foreground" disabled>
                        Manage Subscription Plans (Coming Soon)
                    </Button>
                    <Button variant="outline" className="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => navigate('/')}>
                        Log Out
                    </Button>
                </div>

                <div className="text-center text-xs text-muted-foreground pt-8">
                    Krishna Library Manager v1.2.0
                </div>

            </div>
        </div>
    );
}
